/**
 * MediaLightbox — fullscreen carousel/viewer for product & spare image galleries.
 * - Click backdrop or X to close
 * - ←/→ keys + on-screen arrows to navigate
 * - Mouse-wheel & thumbnails for quick jump
 * - Uses `object-contain` so the full image is visible, never cropped.
 */
import { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight, Play } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { mediaUrl } from "@/lib/media";

export default function MediaLightbox({ items, open, startIndex = 0, onClose }) {
  const [idx, setIdx] = useState(startIndex);

  useEffect(() => { if (open) setIdx(startIndex); }, [open, startIndex]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % items.length);
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + items.length) % items.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, items.length, onClose]);

  if (!items?.length) return null;
  const cur = items[idx] || items[0];
  const next = () => setIdx((i) => (i + 1) % items.length);
  const prev = () => setIdx((i) => (i - 1 + items.length) % items.length);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[200] bg-black/95 flex flex-col"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
          data-testid="media-lightbox"
        >
          {/* Top bar */}
          <div className="flex items-center justify-between px-5 py-4 text-white" onClick={(e) => e.stopPropagation()}>
            <div className="text-xs uppercase tracking-widest font-bold text-gray-300" data-testid="lightbox-counter">
              {idx + 1} / {items.length}
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center hover:bg-white/10 transition-colors"
              aria-label="Close"
              data-testid="lightbox-close"
            ><X className="w-5 h-5" /></button>
          </div>

          {/* Main viewer */}
          <div className="flex-1 flex items-center justify-center px-6 sm:px-16 relative" onClick={(e) => e.stopPropagation()}>
            {items.length > 1 && (
              <button
                onClick={prev}
                className="absolute left-2 sm:left-4 w-11 h-11 sm:w-12 sm:h-12 bg-white/10 hover:bg-[#E65C00] text-white flex items-center justify-center transition-colors"
                aria-label="Previous"
                data-testid="lightbox-prev"
              ><ChevronLeft className="w-6 h-6" /></button>
            )}
            <AnimatePresence mode="wait">
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.2 }}
                className="max-w-[90vw] max-h-[75vh] flex items-center justify-center"
              >
                {cur.type === "image" ? (
                  <img
                    src={mediaUrl(cur.src)}
                    alt=""
                    className="max-w-full max-h-[75vh] object-contain"
                    data-testid="lightbox-image"
                  />
                ) : cur.type === "youtube" && cur.ytid ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${cur.ytid}?autoplay=1`}
                    title="Video"
                    className="w-[90vw] max-w-[1100px] aspect-video"
                    allow="autoplay; encrypted-media; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <video src={mediaUrl(cur.src)} controls autoPlay className="max-w-full max-h-[75vh]" />
                )}
              </motion.div>
            </AnimatePresence>
            {items.length > 1 && (
              <button
                onClick={next}
                className="absolute right-2 sm:right-4 w-11 h-11 sm:w-12 sm:h-12 bg-white/10 hover:bg-[#E65C00] text-white flex items-center justify-center transition-colors"
                aria-label="Next"
                data-testid="lightbox-next"
              ><ChevronRight className="w-6 h-6" /></button>
            )}
          </div>

          {/* Thumbnails */}
          {items.length > 1 && (
            <div className="px-5 pb-5 pt-3" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-2 overflow-x-auto justify-center">
                {items.map((m, i) => (
                  <button
                    key={i}
                    onClick={() => setIdx(i)}
                    className={`w-16 h-16 sm:w-20 sm:h-20 shrink-0 border-2 overflow-hidden relative transition-all ${i === idx ? "border-[#E65C00]" : "border-white/20 hover:border-white/50 opacity-60 hover:opacity-100"}`}
                    aria-label={`Go to image ${i + 1}`}
                    data-testid={`lightbox-thumb-${i}`}
                  >
                    {m.type === "image" ? (
                      <img src={mediaUrl(m.src)} alt="" className="w-full h-full object-cover" />
                    ) : m.type === "youtube" ? (
                      <>
                        <img src={`https://img.youtube.com/vi/${m.ytid}/mqdefault.jpg`} alt="" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center"><Play className="w-4 h-4 text-white fill-white" /></div>
                      </>
                    ) : (
                      <div className="w-full h-full bg-[#0A0A0A] flex items-center justify-center"><Play className="w-4 h-4 text-white fill-white" /></div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
